import React, { useCallback } from "react";

import { faCalendarDays } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Input from "antd/es/input";
import { defineMessages, useIntl } from "react-intl";

import { Activity } from "@mapmycustomers/shared/types/entity";
import { FormItem } from "@mapmycustomers/ui";

import isAutoEmailActivity from "@app/util/email/isAutoEmailActivity";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";

import FormValues from "../../types/FormValues";
import ActivityDate from "../ActivityDate";
import ActivityRepeat from "../ActivityRepeat";
import ActivityTime from "../ActivityTime";

import styles from "./ActivityFooter.module.scss";

const messages = defineMessages({
  autoEmailDisabledTooltip: {
    id: "createActivityModal.footer.scheduleActivity.autoEmail.disabledTooltip",
    defaultMessage:
      "Auto Email time can't be changed because they are logged once an email is successfully sent.",
    description: "Email activities cannot be scheduled",
  },
  disabledTooltip: {
    id: "createActivityModal.footer.scheduleActivity.disabled.tooltip",
    defaultMessage: "Unable to Edit Record",
    description: "Unable to edit record tooltip",
  },
});

interface ActivityFooterProps {
  activity?: Activity;
  disabled?: boolean;
  form: FormInstance<FormValues>;
  hideRecurring?: boolean;
  isFollowUp?: boolean;
  onChange?: () => void;
}

const ActivityFooter = ({
  activity,
  disabled,
  form,
  hideRecurring,
  isFollowUp,
  onChange,
}: ActivityFooterProps) => {
  const intl = useIntl();

  const handleChange = useCallback(() => {
    onChange?.();
  }, [onChange]);

  const dateValue = form.getFieldValue(ActivityFieldName.START_AT);
  const isValidDate = dateValue && Date.parse(dateValue.toString());

  const disabledTooltip = isAutoEmailActivity(activity)
    ? intl.formatMessage(messages.autoEmailDisabledTooltip)
    : disabled
      ? intl.formatMessage(messages.disabledTooltip)
      : undefined;

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <div className={styles.icon}>
          <FontAwesomeIcon icon={faCalendarDays} />
        </div>

        <FormItem
          noStyle
          shouldUpdate={(prevValues, curValues) =>
            prevValues.startAt !== curValues.startAt || prevValues.endAt !== curValues.endAt
          }
        >
          {({ getFieldValue }) => {
            const dateValue = getFieldValue(ActivityFieldName.START_AT);
            const isValidDate = dateValue && Date.parse(dateValue.toString());

            return (
              <div className={styles.group}>
                <ActivityDate
                  disabledTooltip={disabledTooltip}
                  form={form}
                  isFollowUp={isFollowUp}
                  onChange={handleChange}
                />
                {isValidDate && (
                  <>
                    <ActivityTime
                      disabledTooltip={disabledTooltip}
                      form={form}
                      onChange={handleChange}
                    />
                  </>
                )}
              </div>
            );
          }}
        </FormItem>
      </div>

      {!hideRecurring && isValidDate && (
        <ActivityRepeat
          disabled={disabled || isAutoEmailActivity(activity)}
          disableText={disabledTooltip}
          form={form}
          onChange={handleChange}
        />
      )}

      {!hideRecurring && (
        <>
          <FormItem hidden name="recurIntervalType">
            <Input disabled={disabled} />
          </FormItem>
          <FormItem hidden name="isRecurrent">
            <Input disabled={disabled} />
          </FormItem>
        </>
      )}
    </div>
  );
};

export default ActivityFooter;
